import {
    Box,
    Button,
    Divider,
    Theme,
    Typography,
    createStyles,
    makeStyles,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import { MarketplaceNotifications } from '@spike/marketplace-model';

import { FunctionComponent, useState } from 'react';
import { useNonInitialEffect } from '@versiondos/hooks';
import EmailPoint from '../UI/EmailPoint';
import { useMarketplace } from 'hooks';
import { Spinner } from 'components/UI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHotel, faScissors, faTennisBall } from '@fortawesome/pro-regular-svg-icons';
import { faMoon } from '@fortawesome/pro-solid-svg-icons';

export interface Props {
    onEdit?: (notifications: MarketplaceNotifications) => void;
    notifications: MarketplaceNotifications;
    loading: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            display: 'flex',
            [theme.breakpoints.down('sm')]: {
                display: 'block'
            }
        },
        leftColumn: {
            padding: '39px 30px 25px 65px',
            width: '45%',
            borderRight: '#DDDDDD 1px solid;',
            [theme.breakpoints.down('sm')]: {
                padding: '25px 16px',
                width: '100%',
                borderRight: '#DDDDDD 0px solid;'
            }
        },
        rightColumn: {
            width: '55%',
            [theme.breakpoints.down('sm')]: {
                width: '100%'
            }
        },
        description: {
            fontSize: '15px',
            fontWeight: 400,
            lineHeight: '140%',
            marginTop: '5px',
            marginBottom: '15px'
        },
        title: {
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: '100%',
            marginTop: '25px',
            marginBottom: '10px',
            [theme.breakpoints.down('sm')]: {
                fontSize: '16px'
            }
        },
        subtitle: {
            fontSize: '15px',
            fontWeight: 400,
            lineHeight: '140%',
            marginBottom: '25px',
            maxWidth: '439px',
            [theme.breakpoints.down('sm')]: {
                maxWidth: 'auto'
            }
        },

        contentContainer: {
            'display': 'flex',
            'flexDirection': 'column',
            'width': '100%',
            '& .MuiAccordionDetails-root': {
                padding: '0px'
            }
        },

        emailPointsContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            [theme.breakpoints.down('sm')]: {
                '& .MuiOutlinedInput-root': {
                    width: '100% !important'
                },
                'marginBottom': '9px',
                '& > div': {
                    'width': '100%',
                    'marginBottom': '15px',
                    'paddingRight': '12px',
                    '& > div': {
                        'justifyContent': 'flex-end',
                        '& .MuiOutlinedInput-root': {
                            fontSize: '16px',
                            width: '100%'
                        }
                    }
                },
                '& p': {
                    fontSize: '14px',
                    width: '100%',
                    marginTop: '1px'
                }
            },
            [theme.breakpoints.only('md')]: {
                marginBottom: '15px'
            },
            [theme.breakpoints.only('lg')]: {
                marginBottom: '20px'
            },
            [theme.breakpoints.only('xl')]: {
                marginBottom: '30px'
            }
        },
        addPoint: {
            'fontSize': '16px',
            'lineHeight': '30px',
            'fontWeight': 600,
            'color': '#EAB464',
            [theme.breakpoints.down('sm')]: {
                marginTop: '7px',
                marginBottom: '12px'
            },
            '&:hover': {
                cursor: 'pointer'
            },
            '& > span': {
                textTransform: 'none !important'
            }
        },
        divider: {
            marginBottom: '10px',
            marginTop: '15px'
        },
        previewContainer: {
            marginTop: '39px',
            marginBottom: '70px',
            padding: '50px',
            backgroundColor: '#F8F8F8',
            marginLeft: 'auto',
            marginRight: 'auto',
            width: '90%',
            minHeight: '80%',
            [theme.breakpoints.down('sm')]: {
                width: '90%',
                marginTop: '0px',
                marginBottom: '0px',
                padding: '20px'
            }
        },

        emailPreviewContainer: {
            backgroundColor: '#fff',
            borderRadius: '20px',
            marginLeft: 'auto',
            marginRight: 'auto',
            width: '70%',
            minHeight: '500px',
            [theme.breakpoints.down('sm')]: {
                width: '95%'
            }
        },
        previewLogo: {
            width: '152px',
            marginBottom: '40px',
            [theme.breakpoints.down('sm')]: {
                width: '100px'
            }
        },
        emailPreviewContent: {
            padding: '30px'
        },
        previewTitle: {
            fontSize: '24px',
            fontWeight: 500,
            lineHeight: '100%',
            [theme.breakpoints.down('sm')]: {
                fontSize: '18px'
            }
        },
        previewDescription: {
            fontSize: '18px',
            fontWeight: 400,
            lineHeight: '140%',
            margin: '15px 0 15px 0',
            [theme.breakpoints.down('sm')]: {
                fontSize: '14px'
            }
        },
        previewList: {
            marginTop: '30px',
            width: '100%',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
        },
        previewItem: {
            fontSize: '18px',
            fontWeight: 400,
            lineHeight: '140%',
            letterSpacing: '-0.18px',
            marginBottom: '20px',
            [theme.breakpoints.down('sm')]: {
                fontSize: '14px'
            }
        },
        previewFooter: {
            fontSize: '18px',
            fontWeight: 500,
            lineHeight: '100%',
            marginTop: '35px',
            marginBottom: '20px',
            [theme.breakpoints.down('sm')]: {
                fontSize: '14px'
            }
        },
        previewFooterLine1: {
            fontSize: '15px',
            fontWeight: 500,
            lineHeight: '20px',
            [theme.breakpoints.down('sm')]: {
                fontSize: '13px'
            }
        },
        previewFooterLine2: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            [theme.breakpoints.down('sm')]: {
                fontSize: '13px'
            }
        },
        previewFooterLine3: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            textDecoration: 'underline',
            [theme.breakpoints.down('sm')]: {
                fontSize: '13px'
            }
        },
        footerContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '25px'
        },
        footerLogo: {
            width: '100px',
            [theme.breakpoints.down('sm')]: {
                width: '82px'
            }
        },
        addButtonContainer: {
            marginTop: '10px'
        },
        staticContentHeaderTitle: {
            fontSize: '38px',
            fontWeight: 600,
            padding: '24px 0px 24px 0px',
            display: 'block',

            [theme.breakpoints.down('md')]: {
                fontSize: '30px'
            }
        },
        petsSchedule: {
            paddingBottom: '4px',
            borderBottom: '1px solid #ccc',
            marginBottom: '12px',
            fontFamily: 'Helvetica Neue, helvetica, sans-serif, Arial'
        },
        petScheduleName: {
            display: 'block',
            fontSize: '18px',
            lineHeight: '160%'
        },
        petScheduleDate: {
            padding: '10px 13px',
            backgroundColor: '#F8F5F1',
            display: 'block',
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '100%',
            marginBottom: '8px',
            marginTop: '12px'
        },
        serviceTypeTitle: {
            display: 'block',
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '160%',
            borderBottom: '1px solid #D4D4D4',
            marginBottom: '8px',
            paddingBottom: '8px'
        },
        petsServicesContainer: {
            marginBottom: '12px'
        },
        petsScheduleService: {
            display: 'flex',
            gap: 15,
            marginBottom: '8px'
        },
        petsScheduleTime: {
            color: '#7A7A7A',
            fontSize: '16px',
            fontWeight: 500
        },
        petsScheduleServiceName: {
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '160%'
        },
        boardingDetails: {
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            borderBottom: '1px solid #D4D4D4',
            paddingTop: '8px',
            paddingBottom: '16px',
            marginBottom: '12px'
        },
        boardingDetailItem: {
            flex: '1 1 30%',
            display: 'flex',
            flexDirection: 'column',
            minWidth: '120px'
        },
        detailLabel: {
            color: '#7A7A7A',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '6px'
        },
        detailValue: {
            fontSize: '16px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
        },
        petsScheduleAddon: {
            backgroundColor: '#000000',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 700,
            padding: '4px 8px',
            borderRadius: '232px'
        }
    })
);

export const EditConfirmation: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const marketplace = useMarketplace();

    const [notifications, setNotifications] = useState<MarketplaceNotifications>(props.notifications);

    useNonInitialEffect(() => {
        props.onEdit && props.onEdit(notifications);
    }, [notifications]);

    const handleAddPoint = () => {
        if (notifications.emailContent.length < 10) {
            setNotifications(prev => ({
                ...prev,
                emailContent: [...prev.emailContent, '']
            }));
        }
    };

    const handleDeletePoint = (option: string) => {
        setNotifications(prev => ({
            ...prev,
            emailContent: prev.emailContent.filter(line => line !== option)
        }));
    };

    const handleEditPoint = (value: string, index: number) => {
        setNotifications(prev => ({
            ...prev,
            emailContent: prev.emailContent.map((content, i) => (i === index ? value : content))
        }));
    };
    const petsSchedule = (
        <Box className={classes.petsSchedule}>
            <span className={classes.petScheduleName}>
                <strong>Pet Name:</strong> [Pet Name 1]
            </span>
            <span className={classes.petScheduleDate}>Wednesday, 13 Nov 2024</span>
            <span className={classes.serviceTypeTitle}>
                <FontAwesomeIcon icon={faScissors} /> Grooming
            </span>
            <Box className={classes.petsServicesContainer}>
                <Box className={classes.petsScheduleService}>
                    <span className={classes.petsScheduleTime}>10:00am</span>
                    <span className={classes.petsScheduleServiceName}>[Grooming Service Name]</span>
                </Box>
                <Box className={classes.petsScheduleService}>
                    <span className={classes.petsScheduleTime}>10:00am</span>
                    <span className={classes.petsScheduleServiceName}>[Grooming Service Name]</span>
                </Box>
            </Box>
            <span className={classes.serviceTypeTitle}>
                <FontAwesomeIcon icon={faTennisBall} /> Daycare
            </span>
            <Box className={classes.petsServicesContainer}>
                <Box className={classes.petsScheduleService}>
                    <span className={classes.petsScheduleServiceName}>[Daycare Service Name]</span>
                </Box>
                <span className={classes.petsScheduleTime}>Drop-Off</span>
                <br />
                <span className={classes.petsScheduleServiceName}>10:00am</span>
            </Box>
            <span className={classes.serviceTypeTitle}>
                <FontAwesomeIcon icon={faHotel} /> Boarding
            </span>
            <span className={classes.petsScheduleServiceName}>[Boarding Service Name]</span>
            <Box className={classes.boardingDetails}>
                <Box className={classes.boardingDetailItem}>
                    <span className={classes.detailLabel}>Check-In</span>
                    <span className={classes.detailValue}>Mon, Jul 22, 2025</span>
                    <span className={classes.petsScheduleTime}>3:00pm</span>
                </Box>

                <Box className={classes.boardingDetailItem}>
                    <span className={classes.detailLabel}>Check-Out</span>
                    <span className={classes.detailValue}>Wed, Jul 24, 2025</span>
                    <span className={classes.petsScheduleTime}>12:00pm</span>
                </Box>

                <Box className={classes.boardingDetailItem}>
                    <span className={classes.detailLabel}>Nights</span>
                    <span className={classes.detailValue}>
                        <FontAwesomeIcon icon={faMoon} /> 2 Nights
                    </span>
                </Box>
            </Box>
            <Box className={classes.petsScheduleService}>
                <span className={classes.petsScheduleAddon}>Add-on</span>
                <span className={classes.petScheduleName}>[Add-on Name]</span>
            </Box>
            <Box className={classes.petsScheduleService}>
                <span className={classes.petsScheduleAddon}>Product</span>
                <span className={classes.petScheduleName}>[Product Name]</span>
            </Box>
            <Box className={classes.petsScheduleService}>
                <span className={classes.petsScheduleAddon}>Custom</span>
                <span className={classes.petScheduleName}>[Product Name]</span>
            </Box>
            <span className={classes.petScheduleDate}>Thursday, 14 Nov 2024</span>
            <span className={classes.serviceTypeTitle}>
                <FontAwesomeIcon icon={faScissors} /> Grooming
            </span>
            <Box className={classes.petsServicesContainer}>
                <Box className={classes.petsScheduleService}>
                    <span className={classes.petsScheduleTime}>10:00am</span>
                    <span className={classes.petsScheduleServiceName}>[Grooming Service Name]</span>
                </Box>
            </Box>
        </Box>
    );

    const preview = (
        <Box className={classes.previewContainer}>
            <Box className={classes.emailPreviewContainer}>
                <Box className={classes.emailPreviewContent}>
                    <img
                        className={classes.previewLogo}
                        src={marketplace.logos.promoImageSecondaryUrl}
                    />

                    <Typography className={classes.previewTitle}>Hi [Client first name]</Typography>
                    <span className={classes.staticContentHeaderTitle}>
                        <strong>Appointment Confirmed</strong>
                        <Typography className={classes.previewDescription}>
                            Your appointment request has been confirmed. Please review the details below.
                        </Typography>
                    </span>
                    {petsSchedule}
                    <span className={classes.petScheduleName}>
                        <strong>Address:</strong> [Store Address]
                    </span>
                    <Typography className={classes.previewDescription}>
                        {notifications.emailContent.length > 0 && (
                            <Box>
                                Here’s how to prepare for <b>[Pet Name]’s</b> appointment:
                            </Box>
                        )}
                    </Typography>
                    {notifications.emailContent.length > 0 && (
                        <>
                            <Divider />
                            <ul className={classes.previewList}>
                                {notifications.emailContent.map((emailCont, index) => (
                                    <li
                                        key={`preview-${emailCont}${index}`}
                                        className={classes.previewItem}
                                    >
                                        {emailCont}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    <Typography className={classes.previewFooter}>With love,</Typography>
                    <Typography className={classes.previewFooterLine1}>
                        The {marketplace.basics.businessName} Team
                    </Typography>
                    <Typography className={classes.previewFooterLine2}>{marketplace.basics.phone}</Typography>
                    <Typography className={classes.previewFooterLine3}>{marketplace.basics.website}</Typography>
                </Box>
            </Box>
            <Box className={classes.footerContainer}>
                <img
                    className={classes.footerLogo}
                    src="/images/logo/poweredByCuddles.png"
                />
            </Box>
        </Box>
    );
    return (
        <Box className={classes.container}>
            {props.loading && <Spinner />}
            {!props.loading && (
                <>
                    <Box className={classes.leftColumn}>
                        <Typography className={classes.description}>
                            This email notification automatically sends to clients when their appointment is confirmed.
                        </Typography>
                        <Divider />
                        <Typography className={classes.title}>
                            {isMobile ? 'Client Notifications' : 'Email Content'}
                        </Typography>
                        <Typography className={classes.subtitle}>
                            Mention how you expect pet parents to prepare before each appointment.
                        </Typography>
                        <Box className={classes.contentContainer}>
                            <Box className={classes.emailPointsContainer}>
                                {notifications.emailContent.map((emailCont, index) => (
                                    <Box key={index}>
                                        <EmailPoint
                                            key={`${emailCont}${index}`}
                                            index={index}
                                            optionName={emailCont}
                                            checked={true}
                                            onEditName={handleEditPoint}
                                            editable
                                            onDelete={handleDeletePoint}
                                            isMobile={isMobile}
                                        />
                                        <Divider className={classes.divider} />
                                    </Box>
                                ))}
                            </Box>
                            <Box className={classes.addButtonContainer}>
                                {notifications.emailContent.length < 10 && (
                                    <Button
                                        className={classes.addPoint}
                                        onClick={handleAddPoint}
                                    >
                                        + Add Point
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Box className={classes.rightColumn}>{preview}</Box>
                </>
            )}
        </Box>
    );
};

export default EditConfirmation;
